import { useQueries, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { boardQueryOptions } from '@/entities/board'
import { employeesQueryOptions, meQueryOptions } from '@/entities/employee'
import { myTasksQueryOptions } from '@/entities/task'
import { runningQueryOptions } from '@/entities/time-entry'
import { CreateTaskDialog } from '@/features/create-task'
import { PersonalBoardLink } from '@/features/open-personal-board'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { FilterTab } from '@/shared/ui/filter-tab'
import { Icon } from '@/shared/ui/icon'
import { Menu, MenuItem } from '@/shared/ui/menu'
import { PageHeader } from '@/shared/ui/page-header'
import { MyTasksView, type Running, type SortKey, type TaskFilter } from '@/widgets/my-tasks'

interface MyTasksSearch {
  filter?: TaskFilter
  sort?: SortKey
}

export const Route = createFileRoute('/')({
  component: MyTasksPage,
  head: () => ({ meta: [{ title: 'Мои задачи · STIT Task' }] }),
  // Фильтр/сортировка живут в URL (шарятся, переживают перезагрузку). Дефолты
  // (`all`/`due`) в схему не пишем — URL чистый в дефолтном виде.
  validateSearch: (s: Record<string, unknown>): MyTasksSearch => ({
    ...(s.filter === 'in-work' ||
    s.filter === 'waiting' ||
    s.filter === 'overdue' ||
    s.filter === 'done'
      ? { filter: s.filter }
      : {}),
    ...(s.sort === 'time' || s.sort === 'updated' ? { sort: s.sort } : {}),
  }),
})

const FILTERS: { value: TaskFilter; label: string }[] = [
  { value: 'all', label: 'Всё' },
  { value: 'in-work', label: 'В работе' },
  { value: 'waiting', label: 'Ждёт ответа' },
  { value: 'overdue', label: 'Просрочено' },
  { value: 'done', label: 'Выполненные' },
]

const SORTS: { value: SortKey; label: string }[] = [
  { value: 'due', label: 'по сроку' },
  { value: 'time', label: 'по времени' },
  { value: 'updated', label: 'по обновлению' },
]

const SKELETON_SECTIONS = ['s1', 's2']
const SKELETON_ROWS = ['a', 'b', 'c']

const ACTION_LINK =
  'inline-flex items-center justify-center gap-2 rounded-control border border-border-strong px-3 pt-[9px] pb-[7px] font-mono text-xs font-medium uppercase tracking-wide text-text-primary transition-colors hover:border-text-primary hover:bg-bg-elevated disabled:opacity-60'

function Header({ onCreate }: { onCreate?: () => void }) {
  return (
    <PageHeader
      title="Мои задачи"
      eyebrow="Сегодня"
      actions={
        <>
          {/* Личная доска — куда уходит «непонятная задачка», которой пока нет
              места ни в одном проекте. Вход отсюда: список задач — то место, где
              такая мысль и появляется. */}
          <PersonalBoardLink className={ACTION_LINK}>
            <Icon name="kanban" size={14} />
            Моя доска
          </PersonalBoardLink>
          <Link to="/focus" className={ACTION_LINK}>
            <Icon name="focus" size={14} />
            Фокус
          </Link>
          {onCreate && (
            <Button size="sm" onClick={onCreate}>
              <Icon name="plus" size={14} />
              Задача
            </Button>
          )}
        </>
      }
    />
  )
}

function Controls({
  filter,
  sort,
  onFilter,
  onSort,
}: {
  filter: TaskFilter
  sort: SortKey
  onFilter: (f: TaskFilter) => void
  onSort: (s: SortKey) => void
}) {
  const sortLabel = SORTS.find((s) => s.value === sort)?.label ?? 'по сроку'
  return (
    <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-b border-border-subtle pb-px">
      <div className="flex flex-wrap items-center gap-5">
        {FILTERS.map((f) => (
          <FilterTab key={f.value} active={filter === f.value} onClick={() => onFilter(f.value)}>
            {f.label}
          </FilterTab>
        ))}
      </div>
      <Menu
        align="end"
        ariaLabel="Сортировка"
        triggerClassName="text-text-secondary hover:text-text-primary"
        trigger={
          <span className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wide">
            {sortLabel}
            <Icon name="chevron-down" size={12} />
          </span>
        }
      >
        {(close) =>
          SORTS.map((s) => (
            <MenuItem
              key={s.value}
              onSelect={() => {
                onSort(s.value)
                close()
              }}
            >
              {s.label}
            </MenuItem>
          ))
        }
      </Menu>
    </div>
  )
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-10">
      {SKELETON_SECTIONS.map((s) => (
        <div key={s}>
          <div className="h-4 w-40 animate-pulse rounded-control bg-bg-elevated" />
          <div className="mt-4 flex flex-col gap-3">
            {SKELETON_ROWS.map((r) => (
              <div key={r} className="h-16 animate-pulse rounded-card bg-bg-elevated" />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function MyTasksPage() {
  const myTasksQ = useQuery(myTasksQueryOptions)
  const runningQ = useQuery(runningQueryOptions)
  const meQ = useQuery(meQueryOptions)
  const employeesQ = useQuery(employeesQueryOptions)
  const [createOpen, setCreateOpen] = useState(false)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const filter = search.filter ?? 'all'
  const sort = search.sort ?? 'due'

  // Пересобираем весь search (exactOptional запрещает undefined-как-удаление):
  // дефолтные `all`/`due` просто не попадают в объект → чистый URL.
  const setSearch = (next: MyTasksSearch) => void navigate({ search: next })
  const setFilter = (f: TaskFilter) =>
    setSearch({ ...(f === 'all' ? {} : { filter: f }), ...(sort !== 'due' ? { sort } : {}) })
  const setSort = (s: SortKey) =>
    setSearch({ ...(filter !== 'all' ? { filter } : {}), ...(s === 'due' ? {} : { sort: s }) })

  const tasks = myTasksQ.data ?? []
  const boardIds = [...new Set(tasks.map((t) => t.boardId))]
  const boardResults = useQueries({ queries: boardIds.map((id) => boardQueryOptions(id)) })

  if (myTasksQ.isPending) {
    return (
      <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
        <Header />
        <div className="mt-8 max-w-[880px]">
          <ListSkeleton />
        </div>
      </div>
    )
  }

  if (myTasksQ.isError) {
    return (
      <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
        <Header />
        <div className="mt-8 flex flex-col items-center gap-4 rounded-card bg-bg-surface py-14 text-center">
          <p className="text-text-secondary">Не удалось загрузить задачи</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void myTasksQ.refetch()
            }}
          >
            Повторить
          </Button>
        </div>
      </div>
    )
  }

  if (tasks.length === 0) {
    return (
      <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
        <Header onCreate={() => setCreateOpen(true)} />
        <EmptyState
          icon={<Icon name="my-tasks" size={40} />}
          title="Свободно"
          description="Назначенных задач нет — всё разгребено. Заведите новую или возьмите с доски."
          action={
            <Link
              to="/companies"
              className="inline-flex items-center gap-2 rounded-control border border-border-strong px-5 pt-[13px] pb-[11px] font-mono text-sm font-medium text-text-primary uppercase tracking-wide transition-colors hover:border-text-primary hover:bg-bg-elevated"
            >
              <Icon name="kanban" size={14} />
              Взять задачу с доски
            </Link>
          }
        />
        {createOpen && (
          <CreateTaskDialog
            employees={employeesQ.data ?? []}
            onClose={() => setCreateOpen(false)}
            {...(meQ.data?.id ? { defaultAssigneeId: meQ.data.id } : {})}
          />
        )}
      </div>
    )
  }

  const contextPending = runningQ.isPending || boardResults.some((r) => r.isPending)
  const boards = boardResults.flatMap((r) => (r.data ? [r.data] : []))
  const running: Running[] = (runningQ.data ?? []).map((r) => ({
    taskId: r.taskId,
    startedAt: r.startedAt,
    taskTitle: r.taskTitle,
  }))

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <Header onCreate={() => setCreateOpen(true)} />
      <div className="max-w-[880px]">
        <Controls filter={filter} sort={sort} onFilter={setFilter} onSort={setSort} />
        <div className="mt-8">
          {contextPending ? (
            <ListSkeleton />
          ) : (
            <MyTasksView
              tasks={tasks}
              boards={boards}
              running={running}
              meId={meQ.data?.id}
              filter={filter}
              sort={sort}
            />
          )}
        </div>
      </div>
      {/* Место выбирается каскадом компания → проект → доска: список досок «моих»
          задач — не тот набор, куда человек хочет что-то завести. */}
      {createOpen && (
        <CreateTaskDialog
          employees={employeesQ.data ?? []}
          onClose={() => setCreateOpen(false)}
          {...(meQ.data?.id ? { defaultAssigneeId: meQ.data.id } : {})}
        />
      )}
    </div>
  )
}
